import React, { useEffect, useState } from "react";
import { Field, Formik, Form } from "formik";
import * as Yup from "yup";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import AppInput from "components/Input/AppInput";
import { Box, FormGroup, makeStyles, Typography } from "@material-ui/core";
import { YupValidations } from "utils/validate";
import AppButton from "components/CustomButtons/AppButton";
import AppCheckbox from "components/Input/AppCheckbox";
import { fetchSuperFunds } from "store/slices/worker";
import { useDispatch, useSelector } from "react-redux";
import SelectField from "components/Select/SelectField";
import AppColor from "utilities/AppColor";
import AppConfig from "config/AppConfig";
import { useHistory } from "react-router-dom";
import { getAuthInfo } from "store/slices/auth";
import { ACCOUNTING_TYPE } from "config/AppConfig";
const { PATTERNS } = AppConfig;

const styles = makeStyles(() => ({
  title: {
    display: 'flex',
    margin: '2rem 0 1rem 0'
  },
  control: {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'row',
    justifyContent: 'flex-end'
  },
  labelBold: {
    fontWeight: 700,
    margin: '0.5rem 0 0 0'
  },
  boxBlock: {
    background: AppColor.mainLight,
    borderRadius: '0.5rem',
    padding: '0.5rem',
    alignItems: 'center',
    '& .icon': {
      padding: '1rem',
      fontSize: '1.5rem'
    }
  }
}));

const BankAccountForm = (props) => {
  const {
    onSubmit,
    isSubmitting,
    workerDetail,
    changeProgressStatus,
    display,
    disabled
  } = props;

  const { t } = useTranslation();
  const classes = styles();
  const dispatch = useDispatch();
  const history = useHistory();
  const authInfo = useSelector(getAuthInfo);

  const { accountingType } = authInfo;

  const [superFunds, setSuperFunds] = useState([]);
  const [initForm, setInitForm] = useState({
    accountName: '',
    bankName: '',
    bankAccountNumber: '',
    superannuationSuperProviderId: '',
    superannuationAccountNumber: '',
    bankBsbNumber: '',
    taxNumber: '',
    isTaxFreeThresholdClaimed: false
  });

  const validationSchema = Yup.object().shape({
    accountName: Yup.string()
      .trim()
      .required("validation.required")
      .max(32, "validation.max_length_32")
      .matches(PATTERNS.BLANK_SPACES, 'validation.blankSpaces'),
    bankName: Yup.string().trim()
      .required("validation.required")
      .max(32, "validation.max_length_32")
      .matches(PATTERNS.BLANK_SPACES, 'validation.blankSpaces'),
    bankAccountNumber: YupValidations.requiredString,
    superannuationSuperProviderId: accountingType === ACCOUNTING_TYPE.XERO && YupValidations.requiredString,
    superannuationAccountNumber: accountingType === ACCOUNTING_TYPE.XERO && YupValidations.requiredString.max(20, { message: "validation.max_length", value: 20 }),
    bankBsbNumber: YupValidations.requiredString.min(6, { message: "validation.min_length", value: 6 }),
    taxNumber: YupValidations.requiredString.min(8, { message: "validation.min_length", value: 8 })
  });

  const {
    id,
    accountName,
    bankName,
    bankAccountNumber,
    superannuationSuperProviderId = "",
    superannuationAccountNumber = "",
    bankBsbNumber = "",
    taxNumber = "",
    isTaxFreeThresholdClaimed = false
  } = workerDetail;

  useEffect(() => {
    if (workerDetail && Object.keys(workerDetail).length > 0) {
      const initial = {
        accountName,
        bankName,
        bankAccountNumber,
        superannuationSuperProviderId,
        superannuationAccountNumber,
        bankBsbNumber,
        taxNumber,
        isTaxFreeThresholdClaimed
      };
      validationSchema
        .isValid(initial)
        .then((valid) => {
          setTimeout(() => {
            changeProgressStatus(3, valid);
          }, 400);
        });
      setInitForm(initial);
    }
  }, [workerDetail]);

  useEffect(() => {
    if (accountingType === ACCOUNTING_TYPE.XERO) {
      loadSuperFunds();
    }
  }, []);

  const handleSubmitForm = (form) => {
    const params = {
      ...form,
      id
    };
    onSubmit(params);
  };
  const loadSuperFunds = async () => {
    const result = await dispatch(fetchSuperFunds());
    if (result && result.payload) {
      setSuperFunds(result.payload);
    } else {
      if (window.location.pathname.includes('create') && !Object.keys(workerDetail).length > 0) {
        history.push('/admin/workers');
      }
    }
  };

  return (
    <Formik
      enableReinitialize={true}
      onSubmit={handleSubmitForm}
      initialValues={initForm}
      validationSchema={validationSchema}
    >
      {() => {
        return (
          <Form className={`${classes.container} ${!display && 'hidden'}`}>
            <Box className={classes.title}>
              <Typography variant="h5">
                {t("title.bankAccount")}
              </Typography>
            </Box>
            <Box className="mb-2">
              {t('text.provideBankingDetail')}
            </Box>
            <Box display="flex" className={classes.boxBlock}>
              <Box className='icon'>
                <i className="fas fa-lock"></i>
              </Box>
              <Box>
                {t('text.protectBankingDetail')}
              </Box>
            </Box>
            <Box className={classes.labelBold}>
              {t('text.addBankingDetail')}
            </Box>
            <Field
              component={AppInput}
              name="accountName"
              label={"label.accountName"}
              required
              disabled={disabled}
            />
            <Field
              component={AppInput}
              name="bankName"
              label={"label.bankName"}
              required
              disabled={disabled}
            />
            <Field
              component={AppInput}
              name="bankAccountNumber"
              label={"label.accountNumber"}
              type="tel"
              mask={'999999999'}
              maskChar=""
              required
              disabled={disabled}
            />
            <Field
              component={AppInput}
              name="bankBsbNumber"
              label={"label.bsdNumber"}
              type="tel"
              mask={'999999'}
              maskChar=""
              required
              disabled={disabled}
            />
            {accountingType === ACCOUNTING_TYPE.XERO &&
              <>
                <Box className={classes.labelBold}>
                  {t('text.superannuation')}
                </Box>
                <Field
                  component={SelectField}
                  name="superannuationSuperProviderId"
                  label={"label.superProvider"}
                  required
                  disabled={disabled}
                  options={superFunds.map(item => {
                    return {
                      value: item.superFundId,
                      label: item.name
                    }
                  })}
                />
                <Field
                  component={AppInput}
                  name="superannuationAccountNumber"
                  label={"label.account#"}
                  type="tel"
                  mask={'99999999999999999999'}
                  maskChar=""
                  required
                  disabled={disabled}
                />
              </>}
            <Box className={classes.labelBold}>
              {t('label.taxDeclaration')}
            </Box>
            <Field
              component={AppInput}
              name="taxNumber"
              label={"label.taxNumber"}
              type="tel"
              mask={'999999999'}
              maskChar=""
              required
              disabled={disabled}
            />
            <Field
              component={AppCheckbox}
              name="isTaxFreeThresholdClaimed"
              label={"label.taxFreeThresholdClaimed"}
              required
              disabled={disabled}
            />
            {
              !disabled && (
                <FormGroup className={classes.control}>
                  <AppButton
                    label={`${t("button.saveAndContinue")}`}
                    rounder
                    type="submit"
                    isLoading={isSubmitting}
                    disabled={isSubmitting}
                  />
                </FormGroup>
              )
            }
          </Form>
        );
      }}
    </Formik>
  );
};

BankAccountForm.propTypes = {
  onSubmit: PropTypes.func,
  isSubmitting: PropTypes.bool,
  disabled: PropTypes.bool,
  display: PropTypes.bool,
  workerDetail: PropTypes.object,
  changeProgressStatus: PropTypes.func
};

export default BankAccountForm;
